import styled from 'styled-components';
import { useState, useEffect, useRef } from 'react';
import { shadows, device } from '../../../utils/styled-component-mixins';

const BdoSelectInputStyled = styled.div`
  @media ${device.desktop} {
    position: relative;
  }

  .select-wrapper {
    ${(props) => props.isOptionsOpen && `background-color: var(--white); ${shadows.cardShadow};`}
  }

  .select-selected {
    ${(props) =>
      props.isOptionsOpen ? `background-color: var(--white);` : `background-color: transparent;`}
    line-height: 2.4rem;
    padding: 0 1.6rem 0 1.6rem;
    z-index: 100;

    @media ${device.desktop} {
      position: relative;
      padding: 0.8rem 1.6rem 1.6rem 1.6rem;
    }
  }

  .options {
    ${shadows.cardShadow};
    background-color: var(--white);
    display: none;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 2.4rem 4rem 4rem;
    position: absolute;
    right: 50%;
    transform: translateX(50%);

    &.show {
      ${(props) => !props.isMultiSiblingFilter && `right: 50%; transform: translateX(50%);`}
      animation-name: fadeIn;
      animation-duration: 0.5s;
      animation-fill-mode: both;
      column-gap: 3.6rem;
      display: grid;
      flex-direction: column;
      grid-template-columns: auto;
      max-height: 328px;
      row-gap: 1.6rem;
      width: max-content;
      z-index: 99;

      @media ${device.desktop} {
        grid-template-columns: auto auto;
      }
    }

    li {
      color: var(--cta-blue);
      cursor: pointer;
      font-size: 1.4rem;
      font-weight: 400;
      line-height: 2.4rem;
      white-space: nowrap;
      position: relative;

      &:after {
        content: attr(title);
        display: block;
        height: 0;
        visibility: hidden;
        overflow: hidden;
        user-select: none;
        pointer-events: none;
        font-weight: 700;
      }

      &:hover:not(:first-child) {
        font-weight: 700;
      }

      &:first-child {
        color: var(--charcoal-2);
        cursor: default;
      }

      .close-icon {
        margin-left: 0.5rem;
        font-size: 1.4rem;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
      }
    }
  }

  .btn-tertiary {
    ${(props) => props.isOptionsOpen && 'background-color: var(--white);'}
    margin-left: 0;

    span:first-child {
      font-size: 1.8rem;
      max-width: 225px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const BdoSelectInput = (props) => {
  const {
    optionsList,
    onChange,
    isSiblingInputOpen,
    multiSiblingFilterClicked,
    inputIndex,
    isMultiSiblingFilter,
    preselectedOption
  } = props;
  const [selectedOption, setSelectedOption] = useState(preselectedOption);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const parentOfListRef = useRef(null);
  const optionsListRef = useRef(null);
  const buttonRef = useRef(null);

  const handleKeyDown = (index) => (e) => {
    const newIndex = selectedOption === index ? 0 : index;
    switch (e.key) {
      case ' ':
      case 'SpaceBar':
      case 'Enter':
        e.preventDefault();
        setSelectedOption(newIndex);
        onChange(optionsList[newIndex]);
        setIsOptionsOpen(false);
        break;
      default:
        break;
    }
  };

  const onSelectChange = (index) => {
    const newIndex = selectedOption === index ? 0 : index;
    setSelectedOption(newIndex);
    onChange(optionsList[newIndex]);
  };

  useEffect(() => {
    if (!isFirstRender) {
      setSelectedOption(props.updatedValueFromParent);
    } else {
      setIsFirstRender(false);
    }
  }, [props.updatedValueFromParent]);

  useEffect(() => {
    if (isSiblingInputOpen) {
      setIsOptionsOpen(false);
    }
  }, [isSiblingInputOpen]);

  const passIndexToParent = () => {
    setIsOptionsOpen(!isOptionsOpen);
    if (!isOptionsOpen) {
      multiSiblingFilterClicked(inputIndex);
    } else {
      multiSiblingFilterClicked(null);
    }
  };

  const dropDownClicked = () => {
    if (isMultiSiblingFilter) {
      passIndexToParent();
    } else {
      setIsOptionsOpen(!isOptionsOpen);
    }
  };

  useEffect(() => {
    const optionsListElement = optionsListRef.current;
    const buttonElement = buttonRef.current;

    const handler = (event) => {
      if (!optionsListElement?.contains(event.target) && !buttonElement?.contains(event.target)) {
        setIsOptionsOpen(false);
        optionsListElement?.classList.remove('show');
      }
    };

    window.addEventListener('click', handler);

    return () => {
      window.removeEventListener('click', handler);
    };
  });

  useEffect(() => {
    if (isOptionsOpen) {
      const dropDownRect = optionsListRef.current?.getBoundingClientRect();
      if (dropDownRect?.x < 0 && optionsListRef.current) {
        optionsListRef.current.style.left = `${0}`;
        optionsListRef.current.style.transform = `translateX(${0})`;
      }
    }
  }, [isOptionsOpen]);

  return (
    <BdoSelectInputStyled isOptionsOpen={isOptionsOpen} isMultiSiblingFilter={isMultiSiblingFilter}>
      <div className="select-wrapper" ref={parentOfListRef}>
        <div className="select-selected">
          <button
            type="button"
            aria-labelledby={props.ariaLabelledBy}
            aria-haspopup="listbox"
            aria-expanded={isOptionsOpen}
            onClick={() => dropDownClicked()}
            className="non-default-link btn btn-tertiary btn-light-theme btn-reset"
            ref={buttonRef}
          >
            <span>{optionsList[selectedOption]}</span>
            <span className="material-icons-outlined">
              {isOptionsOpen ? 'expand_less' : 'expand_more'}
            </span>
          </button>
        </div>
        <ul
          className={`non-bdo-style-list options ${isOptionsOpen ? 'show' : ''}`}
          role="listbox"
          aria-activedescendant={optionsList[selectedOption]}
          tabIndex={-1}
          ref={optionsListRef}
        >
          {optionsList.map((option, index) => (
            <li
              onClick={() => {
                onSelectChange(index);
                setIsOptionsOpen(false);
              }}
              onKeyDown={handleKeyDown(index)}
              id={option}
              role="option"
              aria-selected={selectedOption === index}
              tabIndex={0}
              key={option}
              title={option}
            >
              {option}
              {selectedOption === index && index !== 0 && (
                <span className="close-icon material-icons-outlined">close</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </BdoSelectInputStyled>
  );
};

export default BdoSelectInput;
